'use client';

import { c as _c } from "react/compiler-runtime";
import { ChevronRight } from 'lucide-react';
import type { Route } from 'next';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { resolveActiveHref } from '@/utils/active-nav';
export type SectionCrumbSection = {
  label: string;
  href: string;
};
export type SectionCrumbProps = {
  sections: SectionCrumbSection[];
};

/**
 * Lightweight contextual crumb shown beside the shop switcher: a chevron plus the label of the nav
 * section the current route belongs to (the most specific match, so `/settings/users` reads "Users",
 * not "Settings"). It complements — never duplicates — the deeper per-page PageHeader breadcrumbs and
 * gives location context on tablet, where the desktop rail is collapsed into the drawer.
 *
 * Renders nothing when no section matches (keeps the bare shop root uncluttered).
 *
 * @param props.sections - Top-level nav sections (label + href) the active route is matched against.
 */
export function SectionCrumb(t0) {
  const $ = _c(10);
  const {
    sections
  } = t0;
  const pathname = usePathname();
  let t1;
  if ($[0] !== pathname || $[1] !== sections) {
    t1 = resolveActiveHref(pathname, sections.map(_temp));
    $[0] = pathname;
    $[1] = sections;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const activeHref = t1;
  let t2;
  if ($[3] !== activeHref || $[4] !== sections) {
    t2 = activeHref ? sections.find(section_0 => section_0.href === activeHref) : null;
    $[3] = activeHref;
    $[4] = sections;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const active = t2;
  if (!active) {
    return null;
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ChevronRight aria-hidden="true" className="h-4 w-4 shrink-0 opacity-50" />;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = active.href as Route;
  let t5;
  if ($[7] !== active.label || $[8] !== t4) {
    t5 = <span className="hidden items-center gap-1.5 text-muted-foreground md:flex">{t3}<Link href={t4} data-testid="section-crumb" className="truncate font-bold text-foreground/80 text-sm uppercase tracking-wide transition-colors hover:text-foreground">{active.label}</Link></span>;
    $[7] = active.label;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
function _temp(section) {
  return section.href;
}